/**
 * CraftingPanel - UI panel for crafting
 */

import { UIPanel } from '../ui/UIPanel';
import { Vector2 } from '../math/Vector2';
import { CraftingSystem } from './CraftingSystem';
import { InventoryComponent } from '../items/InventoryComponent';
import { Recipe } from './Recipe';
import { ItemRegistry } from '../items/ItemRegistry';
import { ItemType } from '../types/enums';
import { t, Localization } from '../systems/Localization';

export class CraftingPanel extends UIPanel {
  private craftingSystem: CraftingSystem;
  private inventory: InventoryComponent | null = null;
  private currentStation: string | null = null;
  private selectedRecipe: Recipe | null = null;
  private recipes: Recipe[] = [];
  private scrollOffset: number = 0;
  private filterCategory: ItemType | null = null;

  private recipeListWidth: number = 220;
  private rowHeight: number = 30;
  private maxScrollOffset: number = 0;

  constructor(craftingSystem: CraftingSystem) {
    super(0, 0, 560, 380);
    this.isDraggable = true;
    this.craftingSystem = craftingSystem;
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  setCurrentStation(station: string | null): void {
    this.currentStation = station;
    this.refreshRecipes();
  }

  show(): void {
    super.show();
    // Reset filter and scroll
    this.filterCategory = null;
    this.scrollOffset = 0;
    this.refreshRecipes();
    // Update title based on station
    this.title = this.currentStation ? t('craft_workbench').toUpperCase() : t('panel_crafting');
    console.log(`CraftingPanel opened. Station: ${this.currentStation}, Recipes: ${this.recipes.length}`);
  }

  /** Handle mouse wheel scrolling */
  handleScroll(delta: number): void {
    if (!this.isVisible) return;

    this.scrollOffset += delta;
    // Clamp scroll offset
    this.scrollOffset = Math.max(0, Math.min(this.scrollOffset, this.maxScrollOffset));
  }

  private refreshRecipes(): void {
    let allRecipes: Recipe[];

    if (this.currentStation === 'workbench') {
      // At workbench: show ALL recipes (basic + workbench)
      allRecipes = this.craftingSystem.getUnlockedRecipes();
    } else {
      // By hand: show only basic recipes (no station required)
      allRecipes = this.craftingSystem.getUnlockedRecipes().filter(r => r.requiredStation === null);
    }

    console.log(`Refreshing recipes for station '${this.currentStation}': found ${allRecipes.length} total`);

    // Apply category filter if set
    if (this.filterCategory !== null) {
      this.recipes = allRecipes.filter(r => r.category === this.filterCategory);
    } else {
      this.recipes = allRecipes;
    }

    console.log(`After filter: ${this.recipes.length} recipes`);

    // Auto-select first recipe
    if (this.recipes.length > 0) {
      this.selectedRecipe = this.recipes[0] ?? null;
    } else {
      this.selectedRecipe = null;
    }

    // Calculate max scroll offset
    const listHeight = this.bounds.height - this.titleBarHeight - 60;
    const totalContentHeight = this.recipes.length * this.rowHeight;
    this.maxScrollOffset = Math.max(0, totalContentHeight - listHeight);

    // Reset scroll if it's now out of bounds
    this.scrollOffset = Math.min(this.scrollOffset, this.maxScrollOffset);
  }

  setFilter(category: ItemType | null): void {
    this.filterCategory = category;
    this.refreshRecipes();
  }

  protected onPanelClick(pos: Vector2): boolean {
    if (!this.inventory) return false;

    // Check recipe list click
    const listX = this.bounds.x + 10;
    const listY = this.bounds.y + this.titleBarHeight + 40;
    const listHeight = this.bounds.height - this.titleBarHeight - 60;

    if (pos.x >= listX && pos.x <= listX + this.recipeListWidth &&
        pos.y >= listY && pos.y <= listY + listHeight) {
      const relY = pos.y - listY + this.scrollOffset;
      if (relY >= 0) {
        const index = Math.floor(relY / this.rowHeight);
        if (index >= 0 && index < this.recipes.length) {
          this.selectedRecipe = this.recipes[index] ?? null;
          return true;
        }
      }
    }

    // Check craft button click
    if (this.selectedRecipe) {
      const craftButtonX = this.bounds.x + this.recipeListWidth + 30;
      const craftButtonY = this.bounds.y + this.bounds.height - 50;
      const craftButtonW = 100;
      const craftButtonH = 35;

      if (pos.x >= craftButtonX && pos.x <= craftButtonX + craftButtonW &&
          pos.y >= craftButtonY && pos.y <= craftButtonY + craftButtonH) {
        this.craftSelected();
        return true;
      }
    }

    // Check filter buttons
    const filterY = this.bounds.y + this.titleBarHeight + 5;
    const filters = this.getFilters();

    for (const filter of filters) {
      if (pos.x >= filter.x && pos.x <= filter.x + 45 &&
          pos.y >= filterY && pos.y <= filterY + 25) {
        this.setFilter(filter.value);
        return true;
      }
    }

    return false;
  }

  private getFilters(): { label: string; value: ItemType | null; x: number }[] {
    return [
      { label: t('craft_filter_all'), value: null, x: this.bounds.x + 10 },
      { label: t('craft_filter_tools'), value: ItemType.Tool, x: this.bounds.x + 55 },
      { label: t('craft_filter_weapons'), value: ItemType.Weapon, x: this.bounds.x + 100 },
      { label: t('craft_filter_gear'), value: ItemType.Armor, x: this.bounds.x + 145 },
      { label: t('craft_filter_items'), value: ItemType.Consumable, x: this.bounds.x + 190 },
      { label: t('craft_filter_ammo'), value: ItemType.Resource, x: this.bounds.x + 235 }
    ];
  }

  private craftSelected(): void {
    if (!this.selectedRecipe || !this.inventory) return;

    const result = this.craftingSystem.craft(this.selectedRecipe, this.inventory, this.currentStation);
    if (result.success) {
      console.log(result.message);
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    // Filter buttons
    this.renderFilters(ctx);

    // Recipe list
    this.renderRecipeList(ctx);

    // Recipe details
    if (this.selectedRecipe) {
      this.renderRecipeDetails(ctx);
    }
  }

  private renderFilters(ctx: CanvasRenderingContext2D): void {
    const y = this.bounds.y + this.titleBarHeight + 5;
    const filters = this.getFilters();

    for (const filter of filters) {
      const isActive = this.filterCategory === filter.value;
      ctx.fillStyle = isActive ? '#4ecca3' : '#555';
      ctx.fillRect(filter.x, y, 50, 25);

      ctx.fillStyle = '#fff';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(filter.label, filter.x + 25, y + 16);
    }
  }

  private renderRecipeList(ctx: CanvasRenderingContext2D): void {
    const listX = this.bounds.x + 10;
    const listY = this.bounds.y + this.titleBarHeight + 40;
    const listHeight = this.bounds.height - this.titleBarHeight - 60;

    // List background
    ctx.fillStyle = 'rgba(40, 40, 60, 0.8)';
    ctx.fillRect(listX, listY, this.recipeListWidth, listHeight);

    // Clip to list area
    ctx.save();
    try {
      ctx.beginPath();
      ctx.rect(listX, listY, this.recipeListWidth, listHeight);
      ctx.clip();

      // Render recipes
      for (let i = 0; i < this.recipes.length; i++) {
        const recipe = this.recipes[i];
        if (!recipe) continue;
        const y = listY + i * this.rowHeight - this.scrollOffset;

        if (y < listY - this.rowHeight || y > listY + listHeight) continue;

        const isSelected = recipe === this.selectedRecipe;
        const canCraft = this.inventory ? this.craftingSystem.canCraft(recipe, this.inventory, this.currentStation) : false;

        // Background
        ctx.fillStyle = isSelected ? 'rgba(78, 204, 163, 0.4)' : 'transparent';
        ctx.fillRect(listX, y, this.recipeListWidth, this.rowHeight);

        // Get localized recipe name
        const itemInfo = Localization.getItem(recipe.result.itemId);
        const recipeName = itemInfo?.name || recipe.name;

        // Text
        ctx.fillStyle = canCraft ? '#fff' : '#666';
        ctx.font = '11px monospace';
        ctx.textAlign = 'left';
        // Truncate if too long
        const maxLen = 28;
        const displayName = recipeName.length > maxLen ? recipeName.substring(0, maxLen - 2) + '..' : recipeName;
        ctx.fillText(displayName, listX + 5, y + 20);

        // Separator
        ctx.strokeStyle = '#444';
        ctx.beginPath();
        ctx.moveTo(listX, y + this.rowHeight);
        ctx.lineTo(listX + this.recipeListWidth, y + this.rowHeight);
        ctx.stroke();
      }
    } finally {
      ctx.restore();
    }

    // Border
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 1;
    ctx.strokeRect(listX, listY, this.recipeListWidth, listHeight);

    // Scrollbar if needed
    if (this.maxScrollOffset > 0) {
      const scrollbarX = listX + this.recipeListWidth - 6;
      const scrollbarHeight = listHeight;
      const thumbHeight = Math.max(20, (listHeight / (listHeight + this.maxScrollOffset)) * scrollbarHeight);
      const thumbY = listY + (this.scrollOffset / this.maxScrollOffset) * (scrollbarHeight - thumbHeight);

      // Scrollbar track
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(scrollbarX, listY, 4, scrollbarHeight);

      // Scrollbar thumb
      ctx.fillStyle = 'rgba(78, 204, 163, 0.7)';
      ctx.fillRect(scrollbarX, thumbY, 4, thumbHeight);
    }
  }

  private renderRecipeDetails(ctx: CanvasRenderingContext2D): void {
    if (!this.selectedRecipe) return;

    const detailX = this.bounds.x + this.recipeListWidth + 30;
    const detailY = this.bounds.y + this.titleBarHeight + 40;

    // Get localized item info for the result
    const resultItemInfo = Localization.getItem(this.selectedRecipe.result.itemId);
    const recipeName = resultItemInfo?.name || this.selectedRecipe.name;
    const recipeDesc = resultItemInfo?.description || this.selectedRecipe.description;

    // Recipe name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 16px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(recipeName, detailX, detailY);

    // Description - wrap to multiple lines
    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    const maxCharsPerLine = 38;
    const descLines = this.wrapText(recipeDesc, maxCharsPerLine);
    let descY = detailY + 20;
    for (const line of descLines) {
      ctx.fillText(line, detailX, descY);
      descY += 14;
    }

    // Ingredients header
    ctx.fillStyle = '#4ecca3';
    ctx.font = 'bold 12px monospace';
    ctx.fillText(t('craft_ingredients'), detailX, descY + 10);

    // Ingredients list
    let ingredientY = descY + 30;
    for (const ingredient of this.selectedRecipe.ingredients) {
      const item = ItemRegistry.get(ingredient.itemId);
      const itemInfo = Localization.getItem(ingredient.itemId);
      const itemName = itemInfo?.name || item?.name || ingredient.itemId;
      const have = this.inventory?.getItemCount(ingredient.itemId) ?? 0;
      const hasEnough = have >= ingredient.quantity;

      ctx.fillStyle = hasEnough ? '#4ecca3' : '#e74c3c';
      ctx.font = '11px monospace';
      ctx.fillText(`• ${itemName} x${ingredient.quantity} (${have})`, detailX + 10, ingredientY);
      ingredientY += 18;
    }

    // Result
    ctx.fillStyle = '#f1c40f';
    ctx.font = 'bold 12px monospace';
    ctx.fillText(t('craft_result'), detailX, ingredientY + 10);

    const resultItem = ItemRegistry.get(this.selectedRecipe.result.itemId);
    const resultInfo = Localization.getItem(this.selectedRecipe.result.itemId);
    const resultName = resultInfo?.name || resultItem?.name || this.selectedRecipe.result.itemId;
    ctx.fillStyle = '#fff';
    ctx.font = '11px monospace';
    ctx.fillText(
      `${resultName} x${this.selectedRecipe.result.quantity}`,
      detailX + 10,
      ingredientY + 28
    );

    // Craft button
    const canCraft = this.inventory ? this.craftingSystem.canCraft(this.selectedRecipe, this.inventory, this.currentStation) : false;
    const buttonX = detailX;
    const buttonY = this.bounds.y + this.bounds.height - 50;
    const buttonW = 110;
    const buttonH = 35;

    ctx.fillStyle = canCraft ? '#4ecca3' : '#555';
    ctx.fillRect(buttonX, buttonY, buttonW, buttonH);

    ctx.strokeStyle = canCraft ? '#7dffcc' : '#666';
    ctx.lineWidth = 2;
    ctx.strokeRect(buttonX, buttonY, buttonW, buttonH);

    ctx.fillStyle = canCraft ? '#000' : '#888';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(t('craft_button'), buttonX + buttonW / 2, buttonY + 23);
  }

  /** Wrap text to multiple lines */
  private wrapText(text: string, maxCharsPerLine: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if (currentLine.length + word.length + 1 <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    // Limit to 3 lines max
    return lines.slice(0, 3);
  }
}
